import { Link, useParams } from 'react-router-dom'
import { api, type PlayerWeek } from '../../api/public'
import { Empty, Failed, Loading } from '../../components/Async'
import { useApi } from '../../components/useApi'
import { withToPar } from '../../components/toPar'
import { usePlayerParam, usePlayerPaths } from '../../components/playerPaths'

const earnedLabel: Record<string, string> = {
  match_won: 'won match',
  match_lost: 'lost match',
  match_halved: 'halved match',
  match_forfeit: 'won by forfeit',
  low_net: 'low net',
  bye: 'bye',
  knockout_advance: 'advanced',
  major_winner: 'major winner',
  major_runner_up: 'major runner-up',
}

/**
 * One player's season, week by week.
 *
 * # Every week, played or not
 *
 * The league's previous software showed a player's season this way: each
 * scheduled night with its handicap, gross, net and points, a night they missed
 * shown empty, and a round the season's best-N rule left out struck through. It
 * answers "how did his season go?" — the statistics' score history lists only
 * the rounds played, and answers something else.
 *
 * # Nothing here decides which rounds count
 *
 * The server marks each night counted or dropped by the same rule the standings
 * total is summed from, so a struck-through row is exactly a round the table
 * left out. Points are decimal strings shown as they arrive.
 */
export function PlayerSeason() {
  const { pathFor } = usePlayerPaths()
  const { playerId, seasonId = '' } = useParams()
  const resolved = usePlayerParam(playerId)
  const season = useApi(
    resolved ? (s) => api.playerSeason(seasonId, resolved, s) : null,
    [seasonId, resolved],
  )

  if (season.state === 'loading') return <Loading what="the season" />
  if (season.state === 'error') return <Failed what="This season" error={season.error} />
  const d = season.data

  return (
    <section>
      <h2>
        <Link to={pathFor(d.player.public_id)}>{d.player.display_name}</Link> · {d.season_name}
      </h2>

      <dl className="stat-grid">
        <div>
          <dt>Season points</dt>
          <dd className="stat-big">{d.points ?? '—'}</dd>
          <dd className="stat-note">
            {d.position === null
              ? 'no night completed yet'
              : `${ordinal(d.position)} in the standings`}
          </dd>
        </div>
        <div>
          <dt>Rounds</dt>
          <dd className="stat-big">{d.rounds_played}</dd>
          <dd className="stat-note">
            played
            {d.counting_rounds !== null &&
              `, best ${d.counting_rounds} count (${d.rounds_counted} counted)`}
          </dd>
        </div>
      </dl>

      {d.weeks.length === 0 ? (
        <Empty>No weeks have been scheduled yet.</Empty>
      ) : (
        <div className="scroll-x">
          <table
            className="table player-season-table table-cards"
            data-testid="player-season-table"
          >
            <thead>
              <tr>
                <th scope="col" className="numeric">
                  Wk
                </th>
                <th scope="col">Date</th>
                <th scope="col">Course</th>
                <th scope="col" className="numeric" title="Strokes received on the holes played">
                  Handicap
                </th>
                <th scope="col" className="numeric">
                  Gross
                </th>
                <th scope="col" className="numeric">
                  Net
                </th>
                <th scope="col" className="numeric">
                  Place
                </th>
                <th scope="col" className="numeric">
                  Points
                </th>
                {d.has_matches && <th scope="col">Result</th>}
              </tr>
            </thead>
            <tbody>
              {d.weeks.map((w) => (
                <WeekRow key={w.event.id} week={w} hasMatches={d.has_matches} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {d.weeks.some((w) => w.dropped) && (
        <p className="note">
          <s>Struck through</s>: a round that does not count toward the season total, because only
          the best {d.counting_rounds} do. It still stands as played.
        </p>
      )}
      {d.weeks.some((w) => w.playing_handicap_overridden) && (
        <p className="note">
          * Handicap set by the commissioner rather than from the handicap index.
        </p>
      )}
    </section>
  )
}

function WeekRow({ week: w, hasMatches }: { week: PlayerWeek; hasMatches: boolean }) {
  const e = w.event
  const missed = e.state === 'complete' && !w.played
  // **A scored week has points, finished or not** (FR-065). A provisional week's
  // awards are already in the season total this table sums to, so blanking them
  // would make the page disagree with its own footer.
  const scored = e.state === 'complete' || e.state === 'provisional'
  const className = [w.dropped ? 'week-dropped' : '', missed ? 'week-missed' : '']
    .filter(Boolean)
    .join(' ')
  return (
    <tr className={className || undefined} data-testid={`week-${e.week_number}`}>
      <td className="numeric">{e.week_number}</td>
      <td>
        <time dateTime={e.scheduled_date}>{e.scheduled_date}</time>
        {e.event_type.is_major && (
          <abbr className="major-star" title={e.event_type.display_name}>
            {' '}
            ★
          </abbr>
        )}
      </td>
      <th scope="row">
        <Link to={`/events/${e.id}`}>{e.course}</Link>
        {e.tee && <span className="note"> ({e.tee})</span>}
      </th>
      {!w.played ? (
        <>
          <td className="numeric" colSpan={4}>
            <span className="note">
              {missed ? 'did not play' : e.state === 'scheduled' ? 'not played yet' : 'in progress'}
            </span>
          </td>
          <td className="numeric">{scored ? w.points : ''}</td>
        </>
      ) : (
        <>
          <td className="numeric">
            {w.playing_handicap ?? '—'}
            {w.playing_handicap_overridden && (
              <abbr className="mark" title="Set by the commissioner, not from the handicap index">
                *
              </abbr>
            )}
          </td>
          <td className="numeric">{withToPar(w.gross, w.gross_to_par)}</td>
          <td className="numeric">{withToPar(w.net, w.net_to_par)}</td>
          <td className="numeric">
            {w.finish_rank === null ? '—' : `${w.tied ? 'T' : ''}${w.finish_rank}`}
          </td>
          <td className="numeric strong">{scored ? w.points : ''}</td>
        </>
      )}
      {hasMatches && (
        <td>{w.earned.map((x) => earnedLabel[x] ?? x.replace(/_/g, ' ')).join(', ')}</td>
      )}
    </tr>
  )
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`
}
